import authStorage from "src/utils/authStorage";
/**
 * BotConfigurationChannel
 * Handles real-time bot configuration updates via ActionCable
 */
class BotConfigurationChannel {
  constructor(core) {
    this.core = core;
    this.subscription = null;
    this.handlers = {};
    this.partnerId = null;
  }

  /**
   * Connect to the BotConfigurationChannel
   * @param {string} partnerId - The partner ID
   * @param {string|null} assistantId - The assistant ID (optional, scopes broadcasts to a specific assistant)
   * @param {Object} handlers - Event handlers for configuration updates
   * @returns {Object|null} The subscription object or null if connection failed
   */
  connect(partnerId, assistantId = null, handlers = {}) {
    console.log('BOT CONFIG CHANNEL: Starting connection process', {
      partnerId,
      assistantId,
      coreExists: !!this.core,
      coreInitialized: this.core?.initialized,
      cableExists: !!this.core?.cable,
      cableSubscriptions: !!this.core?.cable?.subscriptions
    });

    // Verificar se o WebSocket está inicializado
    if (!this.core) {
      console.error('BOT CONFIG CHANNEL ERROR: Core service is not available');
      return null;
    }

    if (!this.core.initialized) {
      console.error('BOT CONFIG CHANNEL ERROR: Core service is not initialized');
      return null;
    }

    if (!this.core.cable) {
      console.error('BOT CONFIG CHANNEL ERROR: Cable connection is not available');
      return null;
    }

    if (!this.core.cable.subscriptions) {
      console.error('BOT CONFIG CHANNEL ERROR: Cable subscriptions manager is not available');
      console.log('BOT CONFIG CHANNEL: Cable object:', this.core.cable);
      return null;
    }

    // Disconnect existing subscription if any
    if (this.subscription) {
      console.log('BOT CONFIG CHANNEL: Disconnecting existing subscription');
      this.disconnect();
    }

    this.partnerId = partnerId;
    this.assistantId = assistantId || null;
    this.handlers = handlers;

    console.log('BOT CONFIG CHANNEL: Creating subscription for partner', partnerId, 'assistant', assistantId);
    console.log('BOT CONFIG CHANNEL: Cable subscriptions object:', this.core.cable.subscriptions);

    try {
      // Subscribe to the channel
      const subscriptionParams = {
        channel: 'BotConfigurationChannel',
        partner_id: String(partnerId), // Ensure it's a string
        token: authStorage.getItem('@sacgpt:token')
      };

      // Include assistant_id when provided to scope broadcasts
      if (assistantId) {
        subscriptionParams.assistant_id = String(assistantId);
      }

      console.log('BOT CONFIG CHANNEL: Subscription params:', subscriptionParams);

      this.subscription = this.core.cable.subscriptions.create(
        subscriptionParams,
        {
          // Called when subscription is ready
          connected: () => {
            console.log('BOT CONFIG CHANNEL: Subscription connected successfully');
            if (handlers.onConnected) {
              handlers.onConnected();
            }
          },

          // Called when subscription is terminated
          disconnected: () => {
            console.log('BOT CONFIG CHANNEL: Subscription disconnected');
            if (handlers.onDisconnected) {
              handlers.onDisconnected();
            }
          },

          // Called when data is received
          received: (data) => {
            console.log('BOT CONFIG CHANNEL: Received data', data);
            console.log('BOT CONFIG CHANNEL: Event type:', data.event);
            console.log('BOT CONFIG CHANNEL: Payload keys:', data.payload ? Object.keys(data.payload) : 'no payload');
            this.handleMessage(data);
          },

          // Custom method to request current configuration
          requestConfiguration: function() {
            console.log('BOT CONFIG CHANNEL: Requesting configuration');
            return this.perform('request_configuration');
          }
        }
      );

      // Request initial configuration after connecting
      setTimeout(() => {
        if (this.subscription && this.subscription.requestConfiguration) {
          this.subscription.requestConfiguration();
        }
      }, 500);

      console.log('BOT CONFIG CHANNEL: Subscription created:', this.subscription);

      if (!this.subscription) {
        console.error('BOT CONFIG CHANNEL ERROR: Subscription creation returned null');
        return null;
      }

      return this.subscription;
    } catch (error) {
      console.error('BOT CONFIG CHANNEL ERROR: Failed to connect', error);
      console.error('BOT CONFIG CHANNEL ERROR: Error details:', {
        message: error.message,
        stack: error.stack
      });
      return null;
    }
  }

  /**
   * Disconnect from the BotConfigurationChannel
   */
  disconnect() {
    if (this.subscription) {
      console.log('BOT CONFIG CHANNEL: Disconnecting');
      this.core.cable.subscriptions.remove(this.subscription);
      this.subscription = null;
      this.handlers = {};
      this.partnerId = null;
      this.assistantId = null;
    }
  }

  /**
   * Handle incoming messages
   * @param {Object} data - The message data
   */
  handleMessage(data) {
    const { event, payload } = data;

    console.log(`BOT CONFIG CHANNEL: Handling event '${event}'`);
    console.log('BOT CONFIG CHANNEL: Available handlers:', Object.keys(this.handlers));

    switch (event) {
      case 'configuration_updated':
        console.log('BOT CONFIG CHANNEL: Processing configuration_updated event');
        if (this.handlers.onConfigurationUpdated) {
          console.log('BOT CONFIG CHANNEL: Calling onConfigurationUpdated handler');
          this.handlers.onConfigurationUpdated(payload);
        } else {
          console.log('BOT CONFIG CHANNEL: No handler for onConfigurationUpdated');
        }
        break;

      case 'partner_detail_updated':
        console.log('BOT CONFIG CHANNEL: Processing partner_detail_updated event');
        if (this.handlers.onConfigurationUpdated) {
          console.log('BOT CONFIG CHANNEL: Calling onConfigurationUpdated handler for partner_detail_updated');
          this.handlers.onConfigurationUpdated(payload);
        }
        break;

      case 'activation_status_changed':
        if (this.handlers.onActivationStatusChanged) {
          this.handlers.onActivationStatusChanged(payload);
        }
        break;

      case 'completeness_score_updated':
        if (this.handlers.onCompletenessScoreUpdated) {
          this.handlers.onCompletenessScoreUpdated(payload);
        }
        break;

      case 'validation_status_changed':
        if (this.handlers.onValidationStatusChanged) {
          this.handlers.onValidationStatusChanged(payload);
        }
        break;

      case 'current_configuration':
        // Response to requestConfiguration
        if (this.handlers.onConfigurationReceived) {
          this.handlers.onConfigurationReceived(payload);
        }
        break;

      case 'error':
        console.error('BOT CONFIG CHANNEL ERROR:', payload);
        if (this.handlers.onError) {
          this.handlers.onError(payload);
        }
        break;

      default:
        console.log('BOT CONFIG CHANNEL: Unknown event', event, payload);
    }
  }

  /**
   * Request the current configuration
   */
  requestConfiguration() {
    if (this.subscription && this.subscription.requestConfiguration) {
      this.subscription.requestConfiguration();
    } else {
      console.warn('BOT CONFIG CHANNEL WARNING: Cannot request configuration - not connected');
    }
  }

  /**
   * Check if connected
   * @returns {boolean} True if connected
   */
  isConnected() {
    return !!this.subscription;
  }
}

export default BotConfigurationChannel;